// Shared print-to-PDF helper used by both the Info Center listing and
// the document reader. Builds a print-friendly HTML page from a full
// info-document record and opens it in a new window with the browser's
// native print dialog (which exposes "Save as PDF" everywhere).
//
// Kept dependency-free so we don't have to ship jspdf/html2canvas just
// for this one feature.

const CATEGORY_LABELS = {
  policy: 'Policy',
  sop: 'SOP',
  guide: 'Guide',
  announcement: 'Announcement',
  faq: 'FAQ',
  training: 'Training',
  compliance: 'Compliance',
  benefits: 'Benefits'
}

function escapeHtml (s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function categoryLabel (key) {
  return CATEGORY_LABELS[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—')
}

// Build the complete printable HTML page for a fully-loaded document
// (i.e. the response from GET /api/info-documents/{idOrSlug}). The
// `body_html` field is inserted verbatim because TipTap already
// produced valid markup; everything else is escaped.
export function buildPrintHtml (doc) {
  const title    = escapeHtml(doc.title || 'Document')
  const summary  = escapeHtml(doc.summary || '')
  const category = escapeHtml(categoryLabel(doc.category))
  const published = doc.published_at
    ? new Date(doc.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : ''
  const orgs = Array.isArray(doc.organisation_audience) && doc.organisation_audience.length
    ? doc.organisation_audience.map((o) => escapeHtml(o.name)).join(', ')
    : 'All organisations'
  const body = doc.body_html || ''

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  @page { margin: 22mm 18mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; color: #1f2a44; line-height: 1.65; margin: 0; padding: 18px 28px 36px; max-width: 880px; }
  header.doc-head { border-bottom: 2px solid #0e5b46; padding-bottom: 14px; margin-bottom: 18px; }
  header.doc-head h1 { font-size: 26pt; margin: 0 0 6px; color: #0f172a; letter-spacing: -0.01em; }
  header.doc-head p.summary { font-size: 11pt; color: #475569; margin: 0 0 10px; }
  .doc-meta { font-size: 9.5pt; color: #64748b; display: flex; flex-wrap: wrap; gap: 14px; }
  .doc-meta strong { color: #0e5b46; font-weight: 700; }
  h1, h2, h3, h4 { color: #0f172a; }
  h1 { font-size: 18pt; margin-top: 18pt; }
  h2 { font-size: 14pt; margin-top: 14pt; }
  h3 { font-size: 12pt; margin-top: 12pt; }
  p { margin: 0 0 8pt; }
  ul, ol { margin: 0 0 8pt 18pt; }
  blockquote { border-left: 3px solid #16a07a; background: #f5fbf8; padding: 6pt 10pt; margin: 8pt 0; color: #134e3a; }
  code { background: #f1f5f9; padding: 1px 4px; border-radius: 3px; font-size: 9.5pt; color: #b91c1c; }
  pre { background: #0f172a; color: #e2e8f0; padding: 10pt; border-radius: 6pt; white-space: pre-wrap; word-wrap: break-word; font-size: 9.5pt; }
  pre code { background: transparent; color: inherit; padding: 0; }
  table { border-collapse: collapse; width: 100%; margin: 10pt 0; page-break-inside: auto; }
  table th { background: #e6f4ee; color: #0e5b46; text-align: left; }
  table th, table td { border: 1px solid #cbd5e1; padding: 6pt 8pt; vertical-align: top; }
  table tr:nth-child(even) td { background: #fbfcfe; }
  hr { border: 0; border-top: 1px dashed #cbd5e1; margin: 14pt 0; }
  a { color: #0e5b46; text-decoration: underline; }
  footer.doc-foot { margin-top: 28pt; padding-top: 10pt; border-top: 1px solid #cbd5e1; font-size: 8.5pt; color: #94a3b8; text-align: center; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <header class="doc-head">
    <h1>${title}</h1>
    ${summary ? `<p class="summary">${summary}</p>` : ''}
    <div class="doc-meta">
      <span>Category: <strong>${category}</strong></span>
      ${published ? `<span>Published: <strong>${escapeHtml(published)}</strong></span>` : ''}
      <span>Organisations: <strong>${orgs}</strong></span>
    </div>
  </header>
  <main>${body}</main>
  <footer class="doc-foot">Generated from Info Center · ${escapeHtml(new Date().toLocaleString('en-IN'))}</footer>
</body>
</html>`
}

// Open the print dialog for a fully-loaded document. Returns a string
// reason if it couldn't (pop-up blocked) so callers can surface a
// useful message; returns null on success.
export function openPrintWindow (doc) {
  const w = window.open('', '_blank')
  if (!w) return 'Pop-up blocked. Allow pop-ups for this site to download as PDF.'
  w.document.open()
  w.document.write(buildPrintHtml(doc))
  w.document.close()
  const trigger = () => {
    try { w.focus(); w.print() } catch (_) { /* ignore */ }
  }
  if (w.document.readyState === 'complete') {
    setTimeout(trigger, 300)
  } else {
    w.addEventListener('load', () => setTimeout(trigger, 250))
  }
  return null
}
